import ShoppingBasketOutlinedIcon from '@mui/icons-material/ShoppingBasketOutlined';
import { Button, Theme, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useState } from 'react';
import { useAppSelector } from '../../app/hooks';
import usePromoteDraftCart from '../../hooks/draftCart/usePromoteDraftCart.hooks';
import { selectCanPromoteToOrder } from '../../redux/features/agentMode/agentMode.selector';
import DraftCartItemReview from '../OrderReview/DraftCartItemReview';
import { DraftCartScope } from '../OrderReview/CartScopeContext';
import {
  selectDraftCartItemCollapseStates,
  selectDraftCartItemCount,
  selectDraftCartItems,
  selectDraftCartItemsQuantity,
} from '../../redux/features/draftCart/draftCart.selector';
import Colors from '../../utils/color';
import { HITL3_PADDING } from '../../utils/hitl3Spacing';
import ConsoleCard from './ConsoleCard';

export const MY_CART_TITLE = 'My Cart';
export const MY_CART_NOT_SENT = 'not sent';
/**
 * HITL-1619 — the empty state says what the cart IS, not how to fill it.
 *
 * This read "Copy a line from the AI cart to start building here." until the
 * omnibar was routed. That was invented copy — nothing in the frozen design
 * asks for it — written to describe the only thing the agent could do while the
 * omnibar was disabled in `Shadow`. It turned a temporary limitation into UI
 * that taught the wrong model: My Cart is a cart the agent BUILDS in, and
 * copying a line from the AI is a shortcut for when the AI happened to get one
 * right, never the way in.
 */
export const MY_CART_EMPTY_HINT = 'Nothing here yet.';
/** Labelled for what it does, not for where it sends the cart. */
export const REPLACE_ORDER_LABEL = 'Replace order with My Cart';

const useStyles = makeStyles<Theme>(() => ({
  // Mirrors AgentCartCard's count chip, plus the standing "not sent" reminder
  // that this column has no POS consequence.
  meta: {
    color: Colors.silverChalice,
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  itemsScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: `0 ${HITL3_PADDING}`,
  },
  empty: {
    color: Colors.silverChalice,
    fontSize: '0.7rem',
    padding: HITL3_PADDING,
  },
  // HITL-1615: the cart-level promote. Pinned under the body so it stays
  // reachable however the column is scrolled — the same reasoning that puts the
  // totals in RightSidebar's footer.
  promoteFooter: {
    flexShrink: 0,
    padding: HITL3_PADDING,
    borderTop: `1px solid ${Colors.alto}`,
  },
  promoteButton: {
    width: '100%',
    fontSize: '0.7rem',
    textTransform: 'none',
  },
}));

interface MyCartCardProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * HITL-1614 — MY CART, the agent's own column beside the AI's.
 *
 * Everything here reads `draftCart` (HITL-1611), the slice the AI's hypothesis
 * rebuild cannot reach. Nothing here dispatches `sendOrder`, emits an
 * interception or speaks, which is why its controls stay live in `Shadow` while
 * the rest of the console is refusing the agent's actions.
 *
 * The header says "not sent" permanently rather than on a condition: a second
 * cart on a drive-thru console has to be unambiguous about which one the
 * customer is getting.
 */
const MyCartCard = ({ open: openProp, onOpenChange }: MyCartCardProps = {}) => {
  const classes = useStyles();
  const [openState, setOpenState] = useState(true);
  const open = openProp ?? openState;
  const toggleOpen = () => {
    const next = !open;
    if (openProp === undefined) setOpenState(next);
    onOpenChange?.(next);
  };

  const draftCartItems = useAppSelector(selectDraftCartItems);
  const quantities = useAppSelector(selectDraftCartItemsQuantity);
  const collapseStates = useAppSelector(selectDraftCartItemCollapseStates);
  const itemCount = useAppSelector(selectDraftCartItemCount);

  const canPromote = useAppSelector(selectCanPromoteToOrder);
  const { replaceOrder } = usePromoteDraftCart();

  const itemIds = Object.keys(draftCartItems ?? {})
    .map(Number)
    .sort((a, b) => a - b);

  // HITL-1615: `Intercept` only, and only with something to promote. Replace —
  // not append — is what avoids the duplicate trap: an agent who shadowed the
  // whole order would otherwise double every line.
  const promoteFooter = canPromote ? (
    <div className={classes.promoteFooter}>
      <Button
        className={classes.promoteButton}
        variant="contained"
        size="small"
        disableElevation
        data-testid="replace-order-with-my-cart"
        disabled={!itemIds.length}
        onClick={replaceOrder}
      >
        {REPLACE_ORDER_LABEL}
      </Button>
    </div>
  ) : undefined;

  return (
    <ConsoleCard
      footer={promoteFooter}
      icon={<ShoppingBasketOutlinedIcon />}
      title={MY_CART_TITLE}
      open={open}
      onToggle={toggleOpen}
      testId="my-cart-card"
      headerRight={
        <span className={classes.meta}>
          {itemCount} {itemCount === 1 ? 'item' : 'items'} · {MY_CART_NOT_SENT}
        </span>
      }
    >
      <div className={classes.itemsScroll} data-testid="my-cart-items">
        {/*
          The scope provider is the id-collision fix: both slices number
          `cartItemId` from 1 and `dialogSlice.selectedItemCartId` is a single
          number, so without it a main-cart selection would also highlight and
          scroll the draft row sharing that id.
        */}
        <DraftCartScope>
          {itemIds.length ? (
            itemIds.map((cartItemId) => (
              <DraftCartItemReview
                key={cartItemId}
                cartItem={draftCartItems[cartItemId]}
                quantity={quantities?.[cartItemId] ?? 1}
                collapseState={collapseStates?.[cartItemId]}
              />
            ))
          ) : (
            <Typography className={classes.empty}>
              {MY_CART_EMPTY_HINT}
            </Typography>
          )}
        </DraftCartScope>
      </div>
    </ConsoleCard>
  );
};

export default MyCartCard;
